import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Book,
  BookOpen,
  FileText,
  Calculator,
  Settings,
  LogOut,
  Tag,
  Building2,
  CalendarDays,
  ChevronRight,
  ChevronDown,
  BarChart3,
  SlidersHorizontal,
  MessageSquare,
  Send,
  X,
  Loader2,
  ShieldCheck,
  Repeat,
  Sparkles,
  Lock,
  ArrowDownLeft,
  ArrowUpRight,
  Users,
  Bell,
  Wallet,
  TrendingUp,
  LineChart,
  Cog,
  Gift,
  ListChecks,
  Crown
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { usePasscode } from '../../context/PasscodeContext';
import { useTheme } from '../../context/ThemeContext';
import { feedbackAPI, fetchMediaAsBlob } from '../../services/api';
import { isIOS } from '../../services/native';
import { useNotificationUnreadCountQuery } from '../../hooks/useQueries';

// Admin nav items — each admin module as a separate nav item
const getAdminNavItems = () => [
  { path: '/admin?tab=dashboard', icon: BarChart3, label: 'Dashboard' },
  { path: '/admin?tab=users', icon: Users, label: 'Users' },
  { path: '/admin?tab=leads', icon: Send, label: 'Leads' },
  { path: '/admin?tab=feedback', icon: MessageSquare, label: 'Feedback' },
  { path: '/admin?tab=reports', icon: ShieldCheck, label: 'Reports' },
  { path: '/admin?tab=coupons', icon: Tag, label: 'Coupons' },
  { path: '/admin?tab=plans', icon: SlidersHorizontal, label: 'Plans' },
  { path: '/admin?tab=subscriptions', icon: Crown, label: 'Subscriptions' },
  { path: '/admin?tab=settlements', icon: Wallet, label: 'Settlements' },
  { path: '/admin?tab=notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin?tab=ai-config', icon: Sparkles, label: 'AI Config' },
  { path: '/admin?tab=referrals', icon: Gift, label: 'Referrals' },
  { path: '/admin?tab=blog', icon: FileText, label: 'Blog' },
  { path: '/settings/profile', icon: Settings, label: 'Settings' },
];

// Classic flat nav items (original)
const getNavItems = (isAdmin, referralEnabled, hidePaymentUI) => {
  if (isAdmin) return getAdminNavItems();

  const items = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/accounts', icon: Building2, label: 'Accounts' },
    { path: '/categories', icon: Tag, label: 'Categories' },
    { path: '/transactions', icon: Book, label: 'Transactions' },
    { path: '/recurring', icon: Repeat, label: 'Recurring' },
    { path: '/receivables', icon: ArrowDownLeft, label: 'Receivables' },
    { path: '/debts', icon: ArrowUpRight, label: 'Debts' },
    { path: '/checklist', icon: ListChecks, label: 'Checklist' },
    { path: '/contacts', icon: Users, label: 'Contacts' },
    { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
    { path: '/stats', icon: BarChart3, label: 'Stats' },
    { path: '/trend', icon: LineChart, label: 'Trend' },
    { path: '/ledger', icon: BookOpen, label: 'Ledger' },
    { path: '/insights', icon: Sparkles, label: 'HJ Insights' },
  ];

  items.push({ path: '/configuration', icon: SlidersHorizontal, label: 'Configuration' });
  items.push({ path: '/passcode', icon: Lock, label: 'App Lock' });
  if (!hidePaymentUI && referralEnabled) items.push({ path: '/referrals', icon: Gift, label: 'Referrals' });
  items.push({ path: '/settings/profile', icon: Settings, label: 'Settings' });

  return items;
};

// Admin grouped nav items
const getAdminGroupedNavItems = () => [
  {
    id: 'admin-overview',
    label: 'Overview',
    icon: BarChart3,
    items: [
      { path: '/admin?tab=dashboard', icon: BarChart3, label: 'Dashboard' },
      { path: '/admin?tab=users', icon: Users, label: 'Users' },
      { path: '/admin?tab=leads', icon: Send, label: 'Leads' },
    ]
  },
  {
    id: 'admin-manage',
    label: 'Management',
    icon: ShieldCheck,
    items: [
      { path: '/admin?tab=feedback', icon: MessageSquare, label: 'Feedback' },
      { path: '/admin?tab=reports', icon: ShieldCheck, label: 'Reports' },
      { path: '/admin?tab=coupons', icon: Tag, label: 'Coupons' },
      { path: '/admin?tab=subscriptions', icon: Crown, label: 'Subscriptions' },
      { path: '/admin?tab=settlements', icon: Wallet, label: 'Settlements' },
    ]
  },
  {
    id: 'admin-config',
    label: 'Configuration',
    icon: Cog,
    items: [
      { path: '/admin?tab=plans', icon: SlidersHorizontal, label: 'Plans' },
      { path: '/admin?tab=notifications', icon: Bell, label: 'Notifications' },
      { path: '/admin?tab=ai-config', icon: Sparkles, label: 'AI Config' },
      { path: '/admin?tab=referrals', icon: Gift, label: 'Referrals' },
      { path: '/admin?tab=blog', icon: FileText, label: 'Blog' },
    ]
  },
  {
    id: 'admin-settings',
    label: 'Settings',
    icon: Cog,
    items: [
      { path: '/settings/profile', icon: Settings, label: 'Settings' },
    ]
  },
];

// Modern grouped nav items
const getGroupedNavItems = (isAdmin, referralEnabled, hidePaymentUI) => {
  if (isAdmin) return getAdminGroupedNavItems();

  const groups = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      items: [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: Wallet,
      items: [
        { path: '/accounts', icon: Building2, label: 'Accounts' },
        { path: '/transactions', icon: Book, label: 'Transactions' },
        { path: '/recurring', icon: Repeat, label: 'Recurring' },
        { path: '/categories', icon: Tag, label: 'Categories' },
      ]
    },
    {
      id: 'tracking',
      label: 'Tracking',
      icon: TrendingUp,
      items: [
        { path: '/receivables', icon: ArrowDownLeft, label: 'Receivables' },
        { path: '/debts', icon: ArrowUpRight, label: 'Debts' },
    { path: '/checklist', icon: ListChecks, label: 'Checklist' },
        { path: '/contacts', icon: Users, label: 'Contacts' },
      ]
    },
    {
      id: 'insights',
      label: 'Analytics',
      icon: BarChart3,
      items: [
        { path: '/stats', icon: BarChart3, label: 'Stats' },
        { path: '/trend', icon: LineChart, label: 'Trend' },
        { path: '/ledger', icon: BookOpen, label: 'Ledger' },
        { path: '/insights', icon: Sparkles, label: 'HJ Insights' },
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Cog,
      items: [
        { path: '/configuration', icon: SlidersHorizontal, label: 'Configuration' },
        { path: '/passcode', icon: Lock, label: 'App Lock' },
        ...(!hidePaymentUI && referralEnabled ? [{ path: '/referrals', icon: Gift, label: 'Referrals' }] : []),
        { path: '/settings/profile', icon: Settings, label: 'Settings' },
      ]
    },
  ];

  return groups;
};

// UserAvatar component with fallback for broken images
function UserAvatar({ user, size = 'w-10 h-10', textSize = 'text-base' }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const profilePictureRaw = user?.profilePicture;
  const profilePicture = typeof profilePictureRaw === 'string' ? profilePictureRaw : profilePictureRaw?.url || null;
  const avatar = user?.avatar;
  const initial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  useEffect(() => {
    let blobUrl = null;

    const loadImage = async () => {
      if (!profilePicture) {
        setImageSrc(null);
        return;
      }

      if (profilePicture.startsWith('/api/media')) {
        try {
          blobUrl = await fetchMediaAsBlob(profilePicture);
          setImageSrc(blobUrl);
          setImgError(false);
        } catch (err) {
          console.error('Failed to load profile picture:', err);
          setImgError(true);
        }
      } else {
        setImageSrc(profilePicture);
      }
    };

    loadImage();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [profilePicture]);

  if (imageSrc && !imgError) {
    return (
      <img
        src={imageSrc}
        alt={user?.name || 'Profile'}
        className={`${size} rounded-full object-cover`}
        onError={() => setImgError(true)}
      />
    );
  }

  if (avatar && !avatarError) {
    return (
      <img
        src={avatar}
        alt={user?.name || 'Profile'}
        className={`${size} rounded-full object-cover`}
        onError={() => setAvatarError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full flex items-center justify-center`}
      style={{ backgroundColor: 'var(--accent-bg)' }}
    >
      <span className={`text-white font-semibold ${textSize}`}>{initial}</span>
    </div>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { isTrialing, isActive, freeAccessMode, referralEnabled, hidePaymentUI } = useSubscription();
  const { isPasscodeEnabled, isPasscodeSet, lockDesktop } = usePasscode();
  const { isModernUI } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showSubscription = !hidePaymentUI && !freeAccessMode && (!isActive || isTrialing);

  const { data: unreadCount = 0 } = useNotificationUnreadCountQuery();

  // Check if a nav item is active (special handling for /settings/* and /admin?tab= paths)
  const isItemActive = (itemPath, routerIsActive) => {
    if (itemPath === '/settings/profile') {
      return location.pathname.startsWith('/settings');
    }
    // Admin tab links: /admin?tab=users matches current URL /admin?tab=users
    if (itemPath.startsWith('/admin?tab=')) {
      const tab = itemPath.split('tab=')[1];
      const currentTab = new URLSearchParams(location.search).get('tab');
      return location.pathname === '/admin' && (currentTab === tab || (!currentTab && tab === 'dashboard'));
    }
    return routerIsActive;
  };

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');

  // For modern UI grouped nav - track expanded groups (all open by default)
  const [expandedGroups, setExpandedGroups] = useState(['overview', 'finance', 'tracking', 'insights', 'admin', 'settings', 'admin-overview', 'admin-manage', 'admin-config', 'admin-settings']);

  const handleFeedbackSubmit = async () => {
    if (!feedbackTitle.trim() || !feedbackMessage.trim()) {
      setFeedbackError('Please fill in both title and message');
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError('');

    try {
      await feedbackAPI.create({
        title: feedbackTitle.trim(),
        message: feedbackMessage.trim(),
        type: 'feedback'
      });
      setFeedbackSuccess(true);
      setFeedbackTitle('');
      setFeedbackMessage('');
      setTimeout(() => {
        setShowFeedbackModal(false);
        setFeedbackSuccess(false);
      }, 2000);
    } catch (err) {
      setFeedbackError(err.error || 'Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const openFeedbackModal = () => {
    setShowFeedbackModal(true);
    setFeedbackError('');
    setFeedbackSuccess(false);
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Classic Sidebar UI
  if (!isModernUI) {
    return (
      <aside
        className="hidden xl:flex xl:flex-col xl:w-72 xl:fixed xl:inset-y-0"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-6 py-6">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h1 className="font-bold text-lg text-white">Hisab Journey</h1>
              <p className="text-xs" style={{ color: 'var(--sidebar-text)' }}>Personal Finance</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/notifications')}
            className="relative p-2 rounded-xl transition-colors"
            style={{ color: 'var(--sidebar-text)' }}
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-expense-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* User info */}
        <div
          className="px-4 py-4 mx-4 rounded-xl"
          style={{ backgroundColor: 'var(--sidebar-bg-hover)' }}
        >
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="w-10 h-10" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || 'User'}
              </p>
              {/* FY badge hidden — financialYear filter not active */}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          <p
            className="px-3 mb-3 text-xs font-medium uppercase tracking-wider"
            style={{ color: 'var(--sidebar-text)' }}
          >
            {user?.isAdmin ? 'Admin Panel' : 'Menu'}
          </p>
          {!user?.isAdmin && !hidePaymentUI && !freeAccessMode && (
            <NavLink
              to={showSubscription ? '/pricing' : '/subscription'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  isActive ? 'font-medium shadow-lg' : ''
                }`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive ? 'var(--accent-bg)' : showSubscription ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                color: isActive ? 'var(--accent-text)' : showSubscription ? '#10b981' : 'var(--sidebar-text)',
              })}
            >
              {({ isActive }) => (
                <>
                  <Crown className="w-5 h-5" />
                  <span className="flex-1">{showSubscription ? 'Subscribe' : 'Subscription'}</span>
                  {isActive && <ChevronRight className="w-4 h-4 opacity-70" />}
                </>
              )}
            </NavLink>
          )}
          {getNavItems(user?.isAdmin, referralEnabled, hidePaymentUI).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive: routerActive }) => {
                const active = isItemActive(item.path, routerActive);
                return `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  active ? 'font-medium shadow-lg' : ''
                }`;
              }}
              style={({ isActive: routerActive }) => {
                const active = isItemActive(item.path, routerActive);
                return {
                  backgroundColor: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent-text)' : 'var(--sidebar-text)',
                };
              }}
            >
              {({ isActive: routerActive }) => {
                const active = isItemActive(item.path, routerActive);
                return (
                  <>
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {active && (
                      <ChevronRight className="w-4 h-4 opacity-70" />
                    )}
                  </>
                );
              }}
            </NavLink>
          ))}
        </nav>

        {/* Footer Actions */}
        <div
          className="px-4 py-4 border-t space-y-1"
          style={{ borderColor: 'var(--sidebar-border)' }}
        >
          <button
            onClick={openFeedbackModal}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-primary-600/20 transition-all duration-200"
            style={{ color: 'var(--sidebar-text)' }}
          >
            <MessageSquare className="w-5 h-5" />
            <span>Help / Feedback</span>
          </button>
          {isPasscodeEnabled && isPasscodeSet && (
            <button
              onClick={lockDesktop}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-surface-600/20 transition-all duration-200"
              style={{ color: 'var(--sidebar-text)' }}
            >
              <Lock className="w-5 h-5" />
              <span>Lock App</span>
            </button>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-expense-600/20 transition-all duration-200"
            style={{ color: 'var(--sidebar-text)' }}
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        <FeedbackModal
          show={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          submitting={feedbackSubmitting}
          success={feedbackSuccess}
          error={feedbackError}
          title={feedbackTitle}
          setTitle={setFeedbackTitle}
          message={feedbackMessage}
          setMessage={setFeedbackMessage}
        />
      </aside>
    );
  }

  // Modern Sidebar UI with grouped navigation
  return (
    <aside
      className="hidden xl:flex xl:flex-col xl:w-72 xl:fixed xl:inset-y-0 sidebar-modern"
      style={{ backgroundColor: 'var(--sidebar-bg)' }}
    >
      {/* Logo */}
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-11 h-11 rounded-2xl shadow-lg ring-2 ring-white/10"
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-income-500 rounded-full border-2 border-[var(--sidebar-bg)]" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-white tracking-tight">Hisab Journey</h1>
            <p className="text-xs font-medium" style={{ color: 'var(--sidebar-text)' }}>Personal Finance</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/notifications')}
          className="relative p-2.5 rounded-xl transition-all duration-300 hover:bg-white/10 hover:scale-105 active:scale-95"
          style={{ color: 'var(--sidebar-text)' }}
          title="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-expense-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse shadow-lg shadow-expense-500/30">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* User info */}
      <div
        className="mx-4 p-4 rounded-2xl backdrop-blur-sm border border-white/5 transition-all duration-300 hover:border-white/10"
        style={{ backgroundColor: 'var(--sidebar-bg-hover)' }}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <UserAvatar user={user} size="w-11 h-11" />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-income-500 rounded-full border-2 border-[var(--sidebar-bg-hover)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {user?.name || 'User'}
            </p>
            {/* FY badge hidden — financialYear filter not active */}
          </div>
        </div>
      </div>

      {/* Grouped Navigation */}
      <nav className="flex-1 px-3 py-5 overflow-y-auto scrollbar-hide">
        {!user?.isAdmin && !hidePaymentUI && !freeAccessMode && (
          <div className="mb-2 pl-2">
            <NavLink
              to={showSubscription ? '/pricing' : '/subscription'}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 ${
                  isActive
                    ? showSubscription ? 'font-semibold shadow-lg shadow-emerald-500/20' : 'font-semibold shadow-lg shadow-[var(--accent-bg)]/20'
                    : showSubscription ? 'hover:bg-emerald-500/10' : 'hover:bg-white/5'
                }`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive
                  ? showSubscription ? '#10b981' : 'var(--accent-bg)'
                  : showSubscription ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                color: isActive
                  ? showSubscription ? '#ffffff' : 'var(--accent-text)'
                  : showSubscription ? '#10b981' : 'var(--sidebar-text)',
              })}
            >
              {({ isActive }) => (
                <>
                  <div className={`p-1.5 rounded-lg transition-all duration-300 ${
                    isActive ? 'bg-white/20' : showSubscription ? 'bg-transparent group-hover:bg-emerald-500/10' : 'bg-transparent group-hover:bg-white/5'
                  }`}>
                    <Crown className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-sm">{showSubscription ? 'Subscribe' : 'Subscription'}</span>
                  <ChevronRight className={`w-4 h-4 transition-all duration-300 ${
                    isActive
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0'
                  }`} />
                </>
              )}
            </NavLink>
          </div>
        )}
        {getGroupedNavItems(user?.isAdmin, referralEnabled, hidePaymentUI).map((group) => (
          <div key={group.id} className="mb-2">
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(group.id)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 hover:bg-white/5"
              style={{ color: 'var(--sidebar-text)' }}
            >
              <group.icon className="w-4 h-4 opacity-60" />
              <span className="flex-1 text-xs font-bold uppercase tracking-wider text-left opacity-60">
                {group.label}
              </span>
              <ChevronDown
                className={`w-4 h-4 opacity-40 transition-transform duration-300 ${
                  expandedGroups.includes(group.id) ? 'rotate-0' : '-rotate-90'
                }`}
              />
            </button>

            {/* Group Items */}
            <div
              className={`overflow-hidden transition-all duration-300 ${
                expandedGroups.includes(group.id)
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pl-2 space-y-1 mt-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive: routerActive }) => {
                      const active = isItemActive(item.path, routerActive);
                      return `group flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 ${
                        active
                          ? 'font-semibold shadow-lg shadow-[var(--accent-bg)]/20'
                          : 'hover:bg-white/5'
                      }`;
                    }}
                    style={({ isActive: routerActive }) => {
                      const active = isItemActive(item.path, routerActive);
                      return {
                        backgroundColor: active ? 'var(--accent-bg)' : 'transparent',
                        color: active ? 'var(--accent-text)' : 'var(--sidebar-text)',
                      };
                    }}
                  >
                    {({ isActive: routerActive }) => {
                      const active = isItemActive(item.path, routerActive);
                      return (
                        <>
                          <div className={`p-1.5 rounded-lg transition-all duration-300 ${
                            active
                              ? 'bg-white/20'
                              : 'bg-transparent group-hover:bg-white/5'
                          }`}>
                            <item.icon className="w-4 h-4" />
                          </div>
                          <span className="flex-1 text-sm">{item.label}</span>
                          <ChevronRight className={`w-4 h-4 transition-all duration-300 ${
                            active
                              ? 'opacity-100 translate-x-0'
                              : 'opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0'
                          }`} />
                        </>
                      );
                    }}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        ))}
      </nav>

      {/* Footer Actions */}
      <div
        className="px-3 py-4 mx-3 mb-3 rounded-2xl space-y-1"
        style={{ backgroundColor: 'var(--sidebar-bg-hover)' }}
      >
        <button
          onClick={openFeedbackModal}
          className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-white/10 transition-all duration-300"
          style={{ color: 'var(--sidebar-text)' }}
        >
          <div className="p-1.5 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all duration-300">
            <MessageSquare className="w-4 h-4 text-primary-400" />
          </div>
          <span className="text-sm">Help / Feedback</span>
        </button>
        {isPasscodeEnabled && isPasscodeSet && (
          <button
            onClick={lockDesktop}
            className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-white/10 transition-all duration-300"
            style={{ color: 'var(--sidebar-text)' }}
          >
            <div className="p-1.5 rounded-lg bg-surface-500/20 group-hover:bg-surface-500/30 transition-all duration-300">
              <Lock className="w-4 h-4 text-surface-400" />
            </div>
            <span className="text-sm">Lock App</span>
          </button>
        )}
        <button
          onClick={logout}
          className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-expense-500/20 transition-all duration-300"
          style={{ color: 'var(--sidebar-text)' }}
        >
          <div className="p-1.5 rounded-lg bg-expense-500/20 group-hover:bg-expense-500/30 transition-all duration-300">
            <LogOut className="w-4 h-4 text-expense-400" />
          </div>
          <span className="text-sm">Logout</span>
        </button>
      </div>

      <FeedbackModal
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        submitting={feedbackSubmitting}
        success={feedbackSuccess}
        error={feedbackError}
        title={feedbackTitle}
        setTitle={setFeedbackTitle}
        message={feedbackMessage}
        setMessage={setFeedbackMessage}
      />
    </aside>
  );
}

// Feedback Modal Component (rendered via portal)
function FeedbackModal({ show, onClose, onSubmit, submitting, success, error, title, setTitle, message, setMessage }) {
  if (!show) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-surface-900">Help / Feedback</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-income-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-income-600" />
              </div>
              <h4 className="text-lg font-semibold text-surface-900 mb-2">Thank you!</h4>
              <p className="text-surface-500">Your feedback has been submitted successfully.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-surface-500">
                Share your feedback, report issues, or request features. We value your input!
              </p>

              {error && (
                <div className="p-3 bg-expense-50 text-expense-700 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                  className="w-full px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your feedback, issue, or suggestion in detail..."
                  rows={5}
                  className="w-full px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-surface-400 mt-1 text-right">
                  {message.length}/2000
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-surface-200 rounded-xl text-surface-700 font-medium hover:bg-surface-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
